import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sageApi, type GlStaleLine } from '@/lib/apiClient';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import { downloadCsv } from '@/lib/downloadCsv';

// A READ-ONLY check for cost the Tracker is still counting that Intacct no longer has
// (#613). The GL sync only adds and updates lines, so a line Intacct deletes, or re-dates
// (new record numbers), stays in Actual cost. This asks Intacct which lines still exist
// and lists the rest. It changes nothing here and nothing in Intacct.

const css = `
  .gc-card { background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:var(--ew-s-4); }
  .gc-note { font-size:12.5px; line-height:1.5; color:var(--ew-n-600); margin:0 0 var(--ew-s-3); }
  .gc-row { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
  .gc-btn { font:var(--ew-fw-semibold) 12px/1 var(--ew-font); border-radius:var(--ew-r-md); padding:8px 14px; cursor:pointer; border:1px solid var(--ew-n-300); background:#fff; color:var(--ew-dark); }
  .gc-btn:disabled { opacity:.5; cursor:not-allowed; }
  .gc-sum { font-size:13px; line-height:1.5; margin:var(--ew-s-3) 0 6px; }
  .gc-warn { background:#FBEEE1; border:1px solid #F0D9C0; color:#8A4E12; border-radius:var(--ew-r-md); padding:8px 12px; font-size:12.5px; line-height:1.5; margin:8px 0; }
  .gc-err { color:#B8321C; font-weight:600; font-size:12.5px; margin-top:8px; }
  .gc-scroll { overflow-x:auto; margin-top:8px; }
  .gc-tbl { width:100%; border-collapse:collapse; font-size:12px; }
  .gc-tbl th { text-align:left; padding:6px 8px; font:var(--ew-fw-semibold) 10.5px/1 var(--ew-font); letter-spacing:.05em; text-transform:uppercase; color:var(--ew-n-500); border-bottom:1px solid var(--ew-n-100); white-space:nowrap; }
  .gc-tbl td { padding:6px 8px; border-bottom:1px solid #F2F2F1; white-space:nowrap; font-variant-numeric:tabular-nums; }
  .gc-tbl .num { text-align:right; }
  .gc-kind { font:var(--ew-fw-semibold) 10px/1 var(--ew-font); letter-spacing:.04em; text-transform:uppercase; border-radius:3px; padding:3px 6px; }
  .gc-kind.replaced { background:#e7f0f8; color:#15659E; }
  .gc-kind.removed { background:#FBEEE1; color:#8A4E12; }
`;

const money = (v: number) => (v < 0 ? '-' : '') + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const count = (n: number) => n.toLocaleString('en-US');

const csvCell = (v: string | number | null) => {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** The lines as a CSV a person can sort and hand to accounting. */
export function staleLinesCsv(lines: readonly GlStaleLine[]): string {
  const head = ['Job', 'Entry date', 'Account', 'Cost code', 'Cost type', 'Amount', 'Batch', 'Kind', 'Duplicates record', 'Record', 'Last modified in Intacct', 'Fetched by the Project Tracker'];
  const rows = lines.map((l) => [
    l.projectJob, l.entryDate, l.accountNo, l.taskId, l.costType, l.amount.toFixed(2), l.batchNo,
    l.kind === 'replaced' ? 'Re-dated or re-posted' : 'Removed', l.replacedBy, l.recordNo, l.whenModified, l.fetchedAt,
  ]);
  return [head, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
}

export default function GlStaleCheckPanel() {
  const qc = useQueryClient();
  const [showLines, setShowLines] = useState(false);
  const [allJobs, setAllJobs] = useState(false);

  const q = useQuery({
    queryKey: ['sage-gl-check'],
    queryFn: () => sageApi.glCheck(),
    staleTime: 0,
    refetchInterval: (query) => (query.state.data?.state === 'running' ? 3_000 : false),
  });
  const start = useMutation({
    mutationFn: () => sageApi.startGlCheck(),
    // The run goes on in the background; the poll shows it. A dropped request may still
    // have started one, so re-read rather than trust the failure.
    onSettled: () => qc.invalidateQueries({ queryKey: ['sage-gl-check'] }),
  });

  const s = q.data;
  const running = s?.state === 'running' || start.isPending;
  const r = s?.result ?? null;
  const jobs = r ? (allJobs ? r.byJob : r.byJob.slice(0, 12)) : [];

  return (
    <div className="gc-card">
      <style>{css}</style>
      <p className="gc-note">
        The nightly sync only adds and updates cost lines, so a line that Intacct deletes, or re-dates, stays in a
        job&rsquo;s Actual cost here. This asks Intacct which lines still exist and lists the ones that don&rsquo;t.
        It <b>changes nothing</b>, here or in Intacct. It reads every cost line in Intacct, so it takes a few minutes.
      </p>

      <div className="gc-row">
        {/* Disabled until the state has loaded: a click before then cannot know whether a
            check is already running or whether Sage is configured at all. */}
        <button className="gc-btn" disabled={running || !s || s.configured === false} onClick={() => start.mutate()}>
          {running ? 'Checking…' : 'Check for stale GL lines'}
        </button>
        {s?.state === 'running' && s.stage && <span style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>{s.stage}…</span>}
        {s?.configured === false && <span style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>Sage credentials are not configured on this environment.</span>}
      </div>

      {q.isError && <div className="gc-err">Couldn&rsquo;t read the check&rsquo;s state.</div>}
      {start.isError && <div className="gc-err" role="alert">Couldn&rsquo;t start the check. It may already be running.</div>}
      {s?.state === 'failed' && <div className="gc-err" role="alert">The check failed: {s.error ?? 'unknown error'}. Nothing was changed.</div>}

      {s?.state === 'done' && r && (
        <>
          <div className="gc-sum">
            Checked {s.finishedAt ? fmtDateTime(s.finishedAt) : ''}. The Project Tracker holds <b>{count(r.localLines)}</b> cost lines;
            Intacct has <b>{count(r.intacctLines)}</b>.
            {r.complete && r.staleLinesTotal > 0 ? (
              <> Intacct no longer has <b>{count(r.staleLinesTotal)}</b> of the lines counted here, totaling <b>{money(r.staleAmount)}</b>, across <b>{count(r.byJob.length)}</b> job{r.byJob.length === 1 ? '' : 's'}.</>
            ) : null}
            {r.complete && r.staleLinesTotal === 0 ? <> Every line counted here still exists in Intacct.</> : null}
          </div>

          {!r.complete && (
            <div className="gc-warn" role="alert">
              <b>Not a result.</b> Not all of Intacct&rsquo;s lines were read, so nothing can be called stale. Run the check again.
            </div>
          )}
          {r.warnings.filter((w) => r.complete || !/not all read/i.test(w)).map((w) => (
            <div className="gc-warn" key={w}>{w}</div>
          ))}

          {r.complete && r.staleLinesTotal > 0 && (
            <>
              <div style={{ fontSize: 12, color: 'var(--ew-n-600)', margin: '4px 0' }}>
                <b>Re-dated or re-posted</b> means the entry has a surviving line with the same job, batch, account, cost code and
                amount: the old copy is a duplicate. <b>Removed</b> means nothing like it is left.
              </div>
              <div className="gc-scroll">
                <table className="gc-tbl">
                  <thead>
                    <tr><th>Job</th><th className="num">Lines</th><th className="num">Amount</th><th className="num">Re-dated</th><th className="num">Removed</th></tr>
                  </thead>
                  <tbody>
                    {jobs.map((j) => (
                      <tr key={`${j.projectId}|${j.projectJob}`}>
                        <td>{j.projectJob ?? '—'}</td>
                        <td className="num">{count(j.lines)}</td>
                        <td className="num">{money(j.amount)}</td>
                        <td className="num">{count(j.replaced)}</td>
                        <td className="num">{count(j.removed)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="gc-row" style={{ marginTop: 8 }}>
                {r.byJob.length > 12 && (
                  <button className="gc-btn" onClick={() => setAllJobs((v) => !v)}>{allJobs ? 'Show the largest 12' : `Show all ${r.byJob.length} jobs`}</button>
                )}
                <button className="gc-btn" onClick={() => setShowLines((v) => !v)}>{showLines ? 'Hide the lines' : 'Show the lines'}</button>
                <button className="gc-btn" onClick={() => downloadCsv(staleLinesCsv(r.stale), 'stale-gl-lines')}>Download CSV</button>
              </div>
              {r.staleTruncated && (
                <div className="gc-warn">The list shows the first {count(r.stale.length)} of {count(r.staleLinesTotal)} lines; the totals above cover all of them.</div>
              )}
              {showLines && (
                <div className="gc-scroll">
                  <table className="gc-tbl">
                    <thead>
                      <tr><th>Job</th><th>Date</th><th>Account</th><th>Cost code</th><th className="num">Amount</th><th>Batch</th><th>What happened</th></tr>
                    </thead>
                    <tbody>
                      {r.stale.map((l) => (
                        <tr key={l.recordNo}>
                          <td>{l.projectJob ?? '—'}</td>
                          <td>{l.entryDate ? fmtDate(l.entryDate) : '—'}</td>
                          <td>{l.accountNo ?? '—'}</td>
                          <td>{l.taskId ?? '—'}</td>
                          <td className="num">{money(l.amount)}</td>
                          <td>{l.batchNo ?? '—'}</td>
                          <td><span className={`gc-kind ${l.kind}`}>{l.kind === 'replaced' ? 'Re-dated' : 'Removed'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {r.complete && r.intacctOnlyLines > 0 && (
            <div className="gc-note" style={{ marginTop: 10 }}>
              Intacct also has {count(r.intacctOnlyLines)} line{r.intacctOnlyLines === 1 ? '' : 's'} ({money(r.intacctOnlyAmount)}) the Project
              Tracker has not stored yet: normally entries posted since the last nightly sync.
            </div>
          )}
        </>
      )}
    </div>
  );
}
